import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AIDraftBadge,
  AIRunButton,
  AIWorkingBadge,
  performerResultHasVisibleBadge,
  withCachedCrmWrittenFields,
} from "./shared/AIDraftComponents";
import { useConnectorRunGate } from "./shared/useConnectorRunGate";
import { resolvePerformerItemIds } from "./shared/performerActionInput";
import { useSingleItemPerformerRun } from "./shared/useSingleItemPerformerRun";
import RunErrorNotice from "./shared/RunErrorNotice";
import {
  ACTION_ITEM_CONTEXT_MARKER,
  buildActionItemSourceDeepLink,
  buildActionItemSourceProvenanceParts,
  CAPTURE_WORKFLOW_NAME,
  resolveActionItemTexts,
  isCaptureWorkflowItem,
  isManualActionItem,
  resolveActionItemPageUrl,
  resolveActionItemAssigneeLabel,
  formatDueDate,
  resolveActionItemEventDateShort,
  formatActionItemQuietBadgeLabel,
  computeActionItemQuietDays,
  getItemStableId,
  getItemSidebarKey,
  isOverdue,
  isAiActionSupported,
} from "./actionItemHelpers";
import { getActionItemCategory } from "./actionItemCategories";
import { buildCalendarPrefillUrl, getCalendarEventForItem, peekCachedCalendarEvent } from "../businessModeService";
import ActionItemDetailsSidebar from "./ActionItemDetailsSidebar";
import ActionItemDueDateControl from "./shared/ActionItemDueDateControl";
import {
  ActionItemArchiveIcon,
  ActionItemDeleteIcon,
  ActionItemRestoreIcon,
} from "./shared/actionItemArchiveIcons";
import {
  ActionItemStarIcon,
  ActionItemStarFilledIcon,
} from "./shared/actionItemStarIcons";
import ActionItemSourceLink from "./shared/ActionItemSourceLink";

const STATUS_DONE = "done";
const STATUS_IGNORE = "ignore";

const archiveActionButtonClass = (iconOnly, variant) =>
  `action-item-${variant}-btn${iconOnly ? " action-item-row-action-btn--icon" : ""}`;

/**
 * Attach persisted context-files metadata to a performer result row.
 *
 * @example
 * mergePersistedPerformerResultWithContextFiles(
 *   { attachment_ids: ["f1"] },
 *   [{ id: "f1", filename: "KT Guide.pdf" }]
 * );
 * // { attachment_ids: ["f1"], context_files: [{ id: "f1", filename: "KT Guide.pdf" }] }
 */
export const mergePersistedPerformerResultWithContextFiles = (result, contextFiles) => {
  if (!result || typeof result !== "object") return null;
  return {
    ...result,
    context_files: Array.isArray(contextFiles) ? contextFiles : [],
  };
};

/**
 * True when `persisted` is a leftover from a previous classification/run and
 * must not overlay a newer live result (e.g. parked "other" after Try Again
 * reclassified the task as email).
 *
 * @example
 * isSupersededPersistedPerformerResult(
 *   { type: "email", outcome: "success", draft: "Hi Jane" },
 *   { outcome: "parked", outcome_detail: "Not supported yet." }
 * );
 * // true
 */
export const isSupersededPersistedPerformerResult = (live, persisted) => {
  if (!live || !persisted) return false;
  const liveType = String(live.type || "").trim().toLowerCase();
  const persistedType = String(persisted.type || "").trim().toLowerCase();
  if (liveType && persistedType && liveType !== persistedType) return true;
  const liveOutcome = String(live.outcome || "").trim().toLowerCase();
  const persistedOutcome = String(persisted.outcome || "").trim().toLowerCase();
  if (
    persistedOutcome &&
    liveOutcome &&
    persistedOutcome !== liveOutcome &&
    (persistedOutcome === "parked" || persistedOutcome === "pass_through")
  ) {
    return true;
  }
  return false;
};

/**
 * Overlay a persisted performer result onto the in-session run result.
 *
 * The hook that produced `live` keeps the original pipeline output. Gmail
 * send/edit (and similar post-run mutations) are written to Postgres and to
 * `aiResultsByItem`, then passed here as `persisted`. Overlaying lets the
 * sidebar remount after a task switch show Sent / the edited draft instead of
 * the pre-send run.
 *
 * A previous parked/other result must not clobber a newer live classification —
 * Try Again can reclassify, and spreading persisted last would flash the old
 * "Not supported yet" panel over the new draft.
 *
 * @example
 * mergeLiveAndPersistedPerformerResult(
 *   { type: "email", draft: "Hi", gmail_draft_id: "d1" },
 *   { type: "email", draft: "Hi Jane", gmail_draft_id: "d1", gmail_sent_at: "2026-08-20T10:00:00Z" }
 * );
 * // { type: "email", draft: "Hi Jane", gmail_draft_id: "d1", gmail_sent_at: "2026-08-20T10:00:00Z" }
 */
export const mergeLiveAndPersistedPerformerResult = (live, persisted) => {
  if (!live) return persisted || null;
  if (!persisted) return live;
  if (isSupersededPersistedPerformerResult(live, persisted)) return live;
  return { ...live, ...persisted };
};

/**
 * Apply an in-session Gmail send/edit patch onto a performer result.
 *
 * Unlike `mergeLiveAndPersistedPerformerResult`, a missing result stays missing
 * so a patch cannot invent a draft-less panel.
 *
 * @example
 * overlayPerformerResultPatch(
 *   { type: "email", draft: "Hi" },
 *   { gmail_sent_at: "2026-08-20T10:00:00Z" }
 * );
 * // { type: "email", draft: "Hi", gmail_sent_at: "2026-08-20T10:00:00Z" }
 */
export const overlayPerformerResultPatch = (result, patch) => {
  if (!result) return result || null;
  if (!patch || typeof patch !== "object") return result;
  return { ...result, ...patch };
};

/**
 * Merge a post-run mutation into the cached performer-result map.
 *
 * Keys both the stable id and the action-item text, matching
 * `buildAiResultsMapFromPerformerState`. Does nothing when there is no
 * existing row to patch (avoids creating a draft-less stub).
 *
 * @example
 * applyPerformerResultPatch(
 *   { "w::k::f": { type: "email", draft: "Hi" } },
 *   { stableId: "w::k::f", actionItem: "Follow up", patch: { gmail_sent_at: "2026-08-20T10:00:00Z" } }
 * );
 * // {
 * //   "w::k::f": { type: "email", draft: "Hi", gmail_sent_at: "2026-08-20T10:00:00Z" },
 * //   "Follow up": { type: "email", draft: "Hi", gmail_sent_at: "2026-08-20T10:00:00Z" },
 * // }
 */
export const applyPerformerResultPatch = (prev, { stableId, actionItem, patch } = {}) => {
  if (!patch || typeof patch !== "object") return prev || {};
  const map = prev && typeof prev === "object" ? prev : {};
  const id = String(stableId || "").trim();
  const text = String(actionItem || "").trim();
  const current = (id && map[id]) || (text && map[text]) || null;
  if (!current || typeof current !== "object") return map;
  const merged = { ...current, ...patch };
  const next = { ...map };
  if (id) next[id] = merged;
  if (text) next[text] = merged;
  return next;
};

/**
 * Index performer-state API rows by stable id and action_item text.
 *
 * @example
 * buildAiResultsMapFromPerformerState({ items: [{ result: { client_item_id: "w::k::f", draft: "Hi" } }] });
 * // { "w::k::f": { client_item_id: "w::k::f", draft: "Hi", context_files: [] } }
 */
export const buildAiResultsMapFromPerformerState = (data) => {
  const rows = Array.isArray(data?.items) ? data.items : [];
  const map = {};
  rows.forEach((row) => {
    const mergedResult = mergePersistedPerformerResultWithContextFiles(
      row?.result,
      row?.context_files
    );
    if (!mergedResult) return;
    const clientId = String(mergedResult?.client_item_id || "").trim();
    const textKey = String(mergedResult?.action_item || "").trim();
    if (clientId) map[clientId] = mergedResult;
    if (textKey) map[textKey] = mergedResult;
  });
  return map;
};

/**
 * Shared action-item row: full webapp (text actions, details in sidebar) or compact extension (icons, inline expand).
 *
 * @example
 * <ActionItemRow layout="full" item={item} onOpenSource={openSidebar} onTitleChange={rename} aiResult={aiResults[id]} />
 * // Title + source/due meta line; click title to rename; row click opens sidebar.
 *
 * @example
 * <ActionItemRow layout="compact" onOpenSource={openTab} onTitleChange={rename} showWorkflowSubtitle />
 * // Same clean task line; notes/AI/source expand in a card below the row.
 */
const ActionItemRow = ({
  item,
  onStatusChange,
  onDeleteItem,
  updating,
  onOpenSource,
  onOpenSourceQuote,
  onOpenDetails,
  aiResult,
  crmSideEffectResult = null,
  aiProgress,
  gmailAuthUrl,
  gmailPerformerReady,
  onGmailPerformerReady,
  onOpenGmailDraft,
  performerBulkRunning = false,
  performerRunState = null,
  /** True while this task's details panel is open: it owns the error banner. */
  errorShownInDetails = false,
  onDismissRunError = null,
  onRunStateChange = null,
  performerResultResetKey = 0,
  layout = "full",
  openSourceInWebapp = false,
  webappBaseUrl = "",
  showWorkflowSubtitle = false,
  workflowNameById,
  workflowTypeById,
  rowRef = null,
  initialExpanded = false,
  focusHighlighted = false,
  keyboardFocused = false,
  justAdded = false,
  showCategoryLabel = false,
  showAiAssist = true,
  performerConfig = null,
  onOpenAiSettings,
  onDueDateChange,
  onPinnedChange,
  onTitleChange,
  onNoteChange,
  onCategoryChange,
  categoryOptions = [],
  onAccountChange,
  accountSuggestions = [],
  onFilterByMeeting,
  onFilterBySource,
  rowSelectable = false,
  rowSelected = false,
  onRowSelectToggle,
  showWorkflowColumn = true,
  exiting = false,
  /** When true, inline Archive / AI controls render as icons (narrow viewport or split list). */
  inlineActionsIconOnly = false,
}) => {
  const isCompact = layout === "compact";
  const expandDetailsInline = isCompact;
  const cleanTaskRow = isCompact || layout === "full";
  const useIconOnlyInlineActions = isCompact || inlineActionsIconOnly;
  const openDetailsHandler = onOpenDetails || (!expandDetailsInline ? onOpenSource : null);
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const titleInputRef = useRef(null);
  const isTitleEditingRef = useRef(false);
  isTitleEditingRef.current = isTitleEditing;
  /** Swallow the row click that follows blur when leaving the title input. */
  const suppressNextRowClickRef = useRef(false);
  const [rowExpanded, setRowExpanded] = useState(Boolean(initialExpanded));
  const {
    result: retryResult,
    crmSideEffect: retryCrmSideEffect,
    progress: retryProgress,
    loading: retryLoading,
    error: retryError,
    dismissError: dismissRetryError,
    run: handleRetry,
  } = useSingleItemPerformerRun(
    item,
    item.workflow_id,
    performerResultResetKey,
    aiResult?.gmail_draft_id,
    onRunStateChange
  );

  const stableIdRef = useRef(null);
  stableIdRef.current = resolvePerformerItemIds(item, item.workflow_id).stableId;
  const retryProgressRef = useRef(retryProgress);
  retryProgressRef.current = retryProgress;
  const retryResultRef = useRef(retryResult);
  retryResultRef.current = retryResult;

  const parentRunState = performerRunState || {};
  const mergedRetryLoading = retryLoading || Boolean(parentRunState.loading);
  const mergedRetryProgress = retryProgress?.steps?.length
    ? retryProgress
    : parentRunState.progress || { steps: [] };
  // Prefer the parent (sidebar-initiated) run: the row hook keeps the previous
  // classification until it is the one that ran, and that stale parked draft
  // would otherwise flash after Try Again reclassifies.
  const mergedRetryResult = parentRunState.loading
    ? null
    : parentRunState.result || retryResult || null;
  const mergedCrmSideEffect = parentRunState.loading
    ? null
    : parentRunState.crmSideEffect || retryCrmSideEffect || null;

  // One error per task, one place: the parent's merged state when it provides one,
  // the row's own hook otherwise. While the details panel is open for this task the
  // panel shows it, not the row.
  const runError = performerRunState ? parentRunState.error || "" : retryError;
  const handleDismissRunError = useCallback(() => {
    dismissRetryError();
    onDismissRunError?.(stableIdRef.current);
  }, [dismissRetryError, onDismissRunError]);

  const { guardedRun, gateModal } = useConnectorRunGate();

  const handleAiRunClick = useCallback(
    (opts) => {
      guardedRun(handleRetry, opts, { aiAssistType: item.ai_assist_type });
    },
    [guardedRun, handleRetry, item.ai_assist_type]
  );

  const activeResult = withCachedCrmWrittenFields(
    mergeLiveAndPersistedPerformerResult(mergedRetryResult, aiResult)
  );
  const activeCrmSideEffect = withCachedCrmWrittenFields(
    mergedCrmSideEffect || crmSideEffectResult || null
  );
  const hasCompletedResult = performerResultHasVisibleBadge(activeResult);
  const canRunAiAssist = isAiActionSupported(item);
  const isWorking = mergedRetryLoading || (performerBulkRunning && !hasCompletedResult);
  const activeProgress = isWorking
    ? (mergedRetryProgress?.steps?.length ? mergedRetryProgress : aiProgress || { steps: [] })
    : { steps: [] };
  const showDraftBadge = hasCompletedResult;
  const prevIsWorkingRef = useRef(false);

  useEffect(() => {
    if (isWorking && expandDetailsInline) setRowExpanded(true);
    prevIsWorkingRef.current = isWorking;
  }, [isWorking, expandDetailsInline]);

  useEffect(() => {
    if (initialExpanded || focusHighlighted) setRowExpanded(true);
  }, [initialExpanded, focusHighlighted]);

  useEffect(() => {
    if (!isTitleEditing || !titleInputRef.current) return;
    titleInputRef.current.focus();
    titleInputRef.current.select();
  }, [isTitleEditing]);

  const isDone = item.status === STATUS_DONE;
  const isIgnored = item.status === STATUS_IGNORE;
  const dueDateLabel = formatDueDate(item.due_date);
  const itemIsOverdue = !isDone && !isIgnored && isOverdue(item);
  const isPinned = Boolean(item?.pinned);
  const isAging = Boolean(item?.is_aging) && !isDone && !isIgnored;
  const isAutoArchived = Boolean(item?.auto_archived) && isIgnored;
  const quietBadgeLabel = isAging
    ? formatActionItemQuietBadgeLabel(computeActionItemQuietDays(item))
    : isAutoArchived
      ? "Auto-archived"
      : "";

  const isManual = isManualActionItem(item);
  const resolvedPageUrl = resolveActionItemPageUrl(item);
  const hasManualSourceContext =
    Boolean(resolvedPageUrl) || Boolean(String(item?.source_snippet || "").trim());
  const sourceProvenanceParts = buildActionItemSourceProvenanceParts(item, {
    workflowTypeById,
  });
  const eventDateShort = resolveActionItemEventDateShort(item);
  const eventDateTitle = eventDateShort
    ? sourceProvenanceParts?.fromClause
      ? `${sourceProvenanceParts.fromClause} on ${eventDateShort}`
      : `Source date: ${eventDateShort}`
    : "";
  const showViewSource =
    (!isManual && item.workflow_id && item.file_id) ||
    (isManual && hasManualSourceContext);
  const sourceDeepLink =
    openSourceInWebapp && showViewSource
      ? buildActionItemSourceDeepLink(item, { baseUrl: webappBaseUrl })
      : null;

  const workflowId = String(item.workflow_id || "").trim();
  const workflowLabel = isManual
    ? "Added by you"
    : isCaptureWorkflowItem(item)
      ? CAPTURE_WORKFLOW_NAME
      : String(item.workflow_name || "").trim() ||
        workflowNameById?.get(workflowId) ||
        workflowId ||
        "Topic";
  const workflowBadgeTitle =
    workflowId && workflowLabel !== workflowId
      ? `${workflowLabel} · ${workflowId}`
      : workflowLabel;

  const { displayTitle, fullActionText, hasExpandableFullTask } = resolveActionItemTexts(item);
  const noteText = String(fullActionText || displayTitle || "").trim();
  const hasNoteSection = Boolean(noteText);
  const categoryLabel = getActionItemCategory(item);
  // Document sources (Notion): flag tasks the page gives to someone else or to no one.
  const otherAssigneeLabel =
    item.ownership === "team"
      ? resolveActionItemAssigneeLabel(item)
      : item.ownership === "unassigned"
        ? "Unassigned"
        : "";

  const calendarPrefillUrl =
    activeResult?.type === "calendar"
      ? String(activeResult.calendar_prefill_url || "").trim() ||
        buildCalendarPrefillUrl({
          ...item,
          action_item: String(activeResult.draft || item.action_item || "").trim(),
          due_date: String(activeResult.suggested_due_date || item.due_date || "").trim(),
          scheduled_start_at: activeResult.scheduled_start_at || null,
          scheduled_end_at: activeResult.scheduled_end_at || null,
        })
      : null;

  const [serverCalendarEvent, setServerCalendarEvent] = useState(
    () => peekCachedCalendarEvent(item.key) ?? null
  );
  const calendarPollRef = useRef(false);
  useEffect(() => {
    const isPersonalCat = item.due_date && activeResult?.type !== "calendar" &&
      !["meet", "call", "follow", "opportunit"].some(
        (kw) => String(item.action_category || "").toLowerCase().includes(kw)
      );
    if (!isPersonalCat || !item.key) return;
    const cachedEvent = peekCachedCalendarEvent(item.key);
    if (cachedEvent !== undefined) {
      setServerCalendarEvent(cachedEvent);
      return;
    }
    if (calendarPollRef.current) return;
    calendarPollRef.current = true;
    let cancelled = false;
    getCalendarEventForItem(item.key).then((event) => {
      if (!cancelled && event) setServerCalendarEvent(event);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [item.key, item.due_date, item.action_category, activeResult?.type]);

  const toggleRowExpanded = () => setRowExpanded((v) => !v);

  const showCategoryInMeta = cleanTaskRow && showCategoryLabel;
  const toggleDetailPanel = () => {
    if (!expandDetailsInline && openDetailsHandler) {
      openDetailsHandler(item);
      return;
    }
    toggleRowExpanded();
  };
  const compactWorkflowMetaLabel = workflowLabel;
  const compactWorkflowMetaTitle = workflowLabel;
  const canExpandRow =
    (isCompact &&
      (onNoteChange || onDueDateChange || onCategoryChange || onAccountChange)) ||
    hasNoteSection ||
    hasExpandableFullTask ||
    Boolean(sourceProvenanceParts) ||
    (showViewSource && Boolean(onOpenSource || sourceDeepLink));
  const sourceProvenanceLink =
    showViewSource && onOpenSource && !sourceDeepLink ? (
      <button
        type="button"
        className="action-item-row__cite-btn action-item-row__cite-btn--inline"
        onClick={(e) => {
          e.stopPropagation();
          onOpenSource?.(item);
        }}
        title="Open original source"
      >
        Source
      </button>
    ) : showViewSource && sourceDeepLink ? (
      <a
        href={sourceDeepLink}
        target="_blank"
        rel="noopener noreferrer"
        className="action-item-row__cite-btn action-item-row__cite-btn--link action-item-row__cite-btn--inline"
        title="See where this came from in Task Mantis"
        onClick={(e) => e.stopPropagation()}
      >
        Source
      </a>
    ) : null;

  const sourceProvenanceLine =
    sourceProvenanceParts || sourceProvenanceLink ? (
      <p
        className="action-item-row__source-provenance"
        title={sourceProvenanceParts?.title}
      >
        {sourceProvenanceParts?.createdLabel ? (
          <>Created on {sourceProvenanceParts.createdLabel}</>
        ) : null}
        {sourceProvenanceParts?.fromClause ? (
          <> from {sourceProvenanceParts.fromClause}</>
        ) : null}
        {sourceProvenanceParts?.eventDateLabel ? (
          <> on {sourceProvenanceParts.eventDateLabel}</>
        ) : null}
        {sourceProvenanceLink ? <> {sourceProvenanceLink}</> : null}
      </p>
    ) : null;
  const expandedFooterContent = sourceProvenanceLine ? (
    <div className="action-item-row-expanded__source-footer">{sourceProvenanceLine}</div>
  ) : null;
  const openRowDetailsOrExpand = () => {
    // Never open/expand while the title input is active.
    if (isTitleEditingRef.current) return;
    if (!expandDetailsInline && openDetailsHandler) {
      openDetailsHandler(item);
      return;
    }
    if (!canExpandRow) return;
    toggleRowExpanded();
  };

  const startTitleEdit = (event) => {
    if (!onTitleChange || updating) return;
    event.preventDefault();
    event.stopPropagation();
    setTitleDraft(displayTitle);
    setIsTitleEditing(true);
  };

  const titleLabel = onTitleChange ? (
    <button
      type="button"
      className="action-item-row__title-label"
      title="Click to edit title"
      aria-label="Edit task title"
      disabled={updating}
      onClick={startTitleEdit}
    >
      {displayTitle}
    </button>
  ) : (
    <span className="action-item-row__title-label">{displayTitle}</span>
  );

  const cancelTitleEdit = (event) => {
    event?.stopPropagation?.();
    setIsTitleEditing(false);
    setTitleDraft("");
  };

  /**
   * Persist the list-row title when it changed; no-op on empty or unchanged text.
   *
   * @example
   * // titleDraft = "  Follow up with Priya  ", displayTitle = "Send the deck"
   * saveTitleEdit(); // onTitleChange(item, "Follow up with Priya")
   */
  const saveTitleEdit = (event) => {
    event?.stopPropagation?.();
    const trimmed = String(titleDraft || "").trim();
    if (!trimmed) {
      cancelTitleEdit();
      return;
    }
    if (trimmed === displayTitle) {
      cancelTitleEdit();
      return;
    }
    setIsTitleEditing(false);
    onTitleChange?.(item, trimmed);
  };

  const handleTitleEditKeyDown = (event) => {
    event.stopPropagation();
    if (event.key === "Enter") {
      event.preventDefault();
      saveTitleEdit(event);
    } else if (event.key === "Escape") {
      event.preventDefault();
      cancelTitleEdit(event);
    }
  };

  /**
   * mousedown runs before input blur. Mark the upcoming click so exiting
   * title edit by clicking the row does not open/toggle the details panel.
   *
   * @example
   * // Editing title, user clicks row padding → suppressNextRowClickRef = true
   * // then blur saves, then click is ignored.
   */
  const handleRowContainerMouseDown = (event) => {
    if (!isTitleEditingRef.current) return;
    if (
      event.target instanceof Element &&
      event.target.closest(
        ".action-item-row__title-edit, .action-item-row__title-edit-input"
      )
    ) {
      return;
    }
    suppressNextRowClickRef.current = true;
    // Keep suppress through the blur→click sequence; clear so later clicks work.
    window.setTimeout(() => {
      suppressNextRowClickRef.current = false;
    }, 300);
  };

  const titleBlock = isTitleEditing ? (
    <div
      className="action-item-row__title-edit"
      data-value={titleDraft}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <input
        ref={titleInputRef}
        type="text"
        className="action-item-row__title-edit-input"
        value={titleDraft}
        disabled={updating}
        aria-label="Task title"
        onChange={(e) => setTitleDraft(e.target.value)}
        onKeyDown={handleTitleEditKeyDown}
        onBlur={saveTitleEdit}
      />
    </div>
  ) : (
    <p
      className={`action-item-row__text${canExpandRow ? " action-item-row__text--expandable" : ""}`}
      title={fullActionText || undefined}
    >
      {titleLabel}
    </p>
  );

  const aiAssistCol = showAiAssist ? (
    <div className="action-item-row__ai-col">
      {isWorking ? (
        <AIWorkingBadge
          iconOnly={useIconOnlyInlineActions}
          expanded={rowExpanded}
          onToggle={toggleDetailPanel}
          stepCount={activeProgress?.steps?.length || 0}
        />
      ) : showDraftBadge ? (
        <AIDraftBadge
          iconOnly={useIconOnlyInlineActions}
          result={activeResult}
          expanded={rowExpanded}
          onToggle={toggleDetailPanel}
        />
      ) : canRunAiAssist ? (
        <AIRunButton
          iconOnly={useIconOnlyInlineActions}
          onClick={handleAiRunClick}
          loading={mergedRetryLoading}
          hasResult={Boolean(activeResult)}
        />
      ) : null}
      {runError && !errorShownInDetails && !(expandDetailsInline && rowExpanded) ? (
        <RunErrorNotice variant="inline" message={runError} onDismiss={handleDismissRunError} />
      ) : null}
    </div>
  ) : null;

  const pinActionButton = onPinnedChange ? (
    <button
      type="button"
      className={`action-item-pin-btn action-item-row-action-btn--icon${
        isPinned ? " action-item-pin-btn--active" : ""
      }`}
      onClick={(e) => {
        e.stopPropagation();
        onPinnedChange(item, !isPinned);
      }}
      disabled={updating}
      title={
        isIgnored
          ? isPinned
            ? "Remove star"
            : "Star and restore — bring back and skip aging"
          : isPinned
            ? "Remove star — allow aging when quiet"
            : "Star — mark important, skip aging"
      }
      aria-label={
        isIgnored && !isPinned
          ? "Star and restore task"
          : isPinned
            ? "Remove star from task"
            : "Star task"
      }
      aria-pressed={isPinned}
    >
      {isPinned ? <ActionItemStarFilledIcon /> : <ActionItemStarIcon />}
    </button>
  ) : null;

  // Link-chain icon: hover/click for notes + the verbatim source quote; clicking the
  // quote opens the details panel at that quote. Needs a source and a handler.
  const sourceQuote = String(item?.source_snippet || "").trim();
  const sourceNoteText = (() => {
    if (!hasExpandableFullTask) return "";
    const withoutContext = String(fullActionText || "").split(ACTION_ITEM_CONTEXT_MARKER)[0].trim();
    return withoutContext && withoutContext !== displayTitle ? withoutContext : "";
  })();
  const sourceLinkProvenance = [
    sourceProvenanceParts?.fromClause,
    sourceProvenanceParts?.eventDateLabel,
  ]
    .filter(Boolean)
    .join(" · ");
  const sourceLinkButton =
    onOpenSourceQuote && showViewSource ? (
      <ActionItemSourceLink
        provenanceLabel={sourceLinkProvenance}
        noteText={sourceNoteText}
        quotes={sourceQuote ? [sourceQuote] : []}
        onOpenQuote={(quote) => onOpenSourceQuote(item, quote)}
        disabled={updating}
      />
    ) : null;

  const archiveActionCol =
    isIgnored && onDeleteItem ? (
      <div className="action-item-row__archive-col">
        {sourceLinkButton}
        {pinActionButton}
        <button
          type="button"
          className={archiveActionButtonClass(useIconOnlyInlineActions, "restore")}
          onClick={(e) => {
            e.stopPropagation();
            onStatusChange(item, "pending");
          }}
          disabled={updating}
          title="Restore to open tasks"
          aria-label="Restore"
        >
          {useIconOnlyInlineActions ? <ActionItemRestoreIcon /> : "Restore"}
        </button>
        <button
          type="button"
          className={archiveActionButtonClass(useIconOnlyInlineActions, "delete")}
          onClick={(e) => {
            e.stopPropagation();
            onDeleteItem(item);
          }}
          disabled={updating}
          title="Delete permanently"
          aria-label="Delete permanently"
        >
          {useIconOnlyInlineActions ? <ActionItemDeleteIcon /> : "Delete"}
        </button>
      </div>
    ) : (
      <div className="action-item-row__archive-col">
        {sourceLinkButton}
        {!isIgnored ? pinActionButton : null}
        <button
          type="button"
          className={archiveActionButtonClass(
            // The archive action is always an icon (no "Archive" text).
            isIgnored ? useIconOnlyInlineActions : true,
            isIgnored ? "restore" : "archive"
          )}
          onClick={(e) => {
            e.stopPropagation();
            onStatusChange(item, isIgnored ? "pending" : STATUS_IGNORE);
          }}
          disabled={updating}
          title={isIgnored ? "Restore to open tasks" : "Archive — remove without completing"}
          aria-label={isIgnored ? "Restore" : "Archive"}
        >
          {isIgnored ? (
            useIconOnlyInlineActions ? <ActionItemRestoreIcon /> : "Restore"
          ) : (
            <ActionItemArchiveIcon />
          )}
        </button>
      </div>
    );

  const taskMetaBlock = cleanTaskRow ? (
    <div className="action-item-row__task-meta">
      <div className="action-item-row__task-meta-main">
        {eventDateShort ? (
          <span className="action-item-row__event-date" title={eventDateTitle}>
            {eventDateShort}
          </span>
        ) : null}
        {dueDateLabel || onDueDateChange ? (
          <div className="action-item-row__due-inline" onClick={(e) => e.stopPropagation()}>
            {onDueDateChange ? (
              <ActionItemDueDateControl
                value={item.due_date}
                disabled={updating}
                onChange={(iso) => onDueDateChange(item, iso)}
              />
            ) : dueDateLabel ? (
              <span className={`action-item-due-label${itemIsOverdue ? " action-item-due-label--overdue" : ""}`}>
                {dueDateLabel}
              </span>
            ) : null}
          </div>
        ) : null}
        {quietBadgeLabel ? (
          <span className="action-item-quiet-label" title={quietBadgeLabel}>
            {quietBadgeLabel}
          </span>
        ) : null}
        {showCategoryInMeta ? (
          <span className="action-item-row__category-inline" title={`Category: ${categoryLabel}`}>
            {categoryLabel}
          </span>
        ) : null}
        {otherAssigneeLabel ? (
          <span className="action-item-row__category-inline" title={`Assigned to: ${otherAssigneeLabel}`}>
            {otherAssigneeLabel}
          </span>
        ) : null}
      </div>
    </div>
  ) : null;

  const inlineTitleActions = cleanTaskRow ? (
    <div
      className="action-item-row__inline-actions"
      onClick={(e) => e.stopPropagation()}
    >
      {aiAssistCol}
      {archiveActionCol}
    </div>
  ) : null;

  const taskBody = (
    <div className="action-item-row__body">
      {isCompact && showWorkflowSubtitle ? (
        <p className="action-item-row__workflow-hint" title={compactWorkflowMetaTitle}>
          {compactWorkflowMetaLabel}
        </p>
      ) : null}
      <div className="action-item-row__title-row">
        {cleanTaskRow ? (
          <div className="action-item-row__title-main">{titleBlock}</div>
        ) : (
          titleBlock
        )}
        {inlineTitleActions}
        {rowSelected ? (
          <span className="action-item-row__selected-badge" aria-label="Selected for bulk action">
            ✓ Selected
          </span>
        ) : null}
      </div>
      {showCategoryLabel && !cleanTaskRow ? (
        <span className="action-item-row__category-pill" title={`Category: ${categoryLabel}`}>
          {categoryLabel}
        </span>
      ) : null}
      {item.suggested_done && !isDone && !isIgnored ? (
        <span
          className="action-item-suggested-chip"
          title={String(
            item.suggestion_reason ||
              "AI thinks this may already be done — confirm by checking it off"
          )}
        >
          AI thinks this is done
        </span>
      ) : null}
      {taskMetaBlock}
    </div>
  );

  const shouldBlockRowExpand = (target) => {
    if (!(target instanceof Element)) return false;
    return Boolean(
      target.closest(
        "button, a, input, label, textarea, select, .action-item-check-btn, .action-item-row__title-edit, .action-item-row__ai-col, .action-item-row__inline-actions, .action-item-note-block__text, .action-item-note-block__edit-wrap, .action-item-details-sidebar, [data-no-select]"
      )
    );
  };

  const handleRowContainerClick = (event) => {
    // Click that exited title edit (mousedown → blur → click): save only, no panel toggle.
    if (suppressNextRowClickRef.current) {
      suppressNextRowClickRef.current = false;
      return;
    }
    // While renaming, ignore row clicks.
    if (isTitleEditingRef.current) return;
    if (shouldBlockRowExpand(event.target)) return;
    if (rowSelectable && onRowSelectToggle && (event.shiftKey || event.metaKey)) {
      onRowSelectToggle(item);
      return;
    }
    openRowDetailsOrExpand();
  };

  return (
    <div
      ref={rowRef}
      data-action-item-id={getItemStableId(item)}
      className={`action-item-row${isDone ? " action-item-row--done" : ""}${
        isIgnored ? " action-item-row--ignored" : ""
      }${isCompact ? " action-item-row--compact" : ""}${
        cleanTaskRow ? " action-item-row--clean-task" : ""
      }${isPinned ? " action-item-row--pinned" : ""}${
        itemIsOverdue ? " action-item-row--overdue" : ""
      }${focusHighlighted ? " action-item-row--focus-highlight" : ""
      }${keyboardFocused ? " action-item-row--keyboard-focused" : ""
      }${justAdded ? " action-item-row--just-added" : ""
      }${rowExpanded ? " action-item-row--expanded" : ""}${
        isTitleEditing ? " action-item-row--title-editing" : ""
      }${rowSelected ? " action-item-row--bulk-selected" : ""}${
        rowSelectable ? " action-item-row--selectable" : ""
      }${exiting ? " action-item-row--exiting" : ""}`}
    >
      <div
        className="action-item-row__grid"
        onMouseDown={handleRowContainerMouseDown}
        onClick={handleRowContainerClick}
      >
        {isIgnored ? (
          <span className="action-item-row__check-spacer" aria-hidden="true" />
        ) : (
          <button
            type="button"
            className={`action-item-check-btn${isDone ? " active" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              onStatusChange(item, isDone ? "pending" : STATUS_DONE);
            }}
            disabled={updating}
            title={isDone ? "Reopen" : "Mark complete"}
            aria-label={isDone ? "Reopen" : "Mark complete"}
          />
        )}

        {taskBody}

        {!isCompact && showWorkflowColumn ? (
          <div className="global-item-workflow-col">
            <span
              className="global-item-workflow-badge"
              title={isManual ? "Added by you" : workflowBadgeTitle}
            >
              {workflowLabel}
            </span>
          </div>
        ) : null}

        {!cleanTaskRow ? (
          <div className="action-item-row__source-col">
            <div className="action-item-row__due-inline" onClick={(e) => e.stopPropagation()}>
              {onDueDateChange ? (
                <ActionItemDueDateControl
                  value={item.due_date}
                  disabled={updating}
                  onChange={(iso) => onDueDateChange(item, iso)}
                />
              ) : dueDateLabel ? (
                <span className={`action-item-due-label${itemIsOverdue ? " action-item-due-label--overdue" : ""}`}>
                  {dueDateLabel}
                </span>
              ) : null}
            </div>
          </div>
        ) : null}

        {!cleanTaskRow ? (
          <>
            {aiAssistCol}
            {archiveActionCol}
          </>
        ) : null}
      </div>

      {expandDetailsInline && rowExpanded ? (
        <div className="action-item-row-expanded">
          <ActionItemDetailsSidebar
            key={getItemSidebarKey(item)}
            className="action-item-details-sidebar--inline action-item-details-sidebar--extension-embed"
            item={item}
            workflowTypeById={workflowTypeById}
            aiResult={activeResult}
            crmSideEffectResult={activeCrmSideEffect}
            aiProgress={activeProgress}
            gmailAuthUrl={gmailAuthUrl}
            gmailPerformerReady={gmailPerformerReady}
            onGmailPerformerReady={onGmailPerformerReady}
            onOpenGmailDraft={onOpenGmailDraft}
            onRetryAi={handleRetry}
            retryLoading={mergedRetryLoading}
            retryError={runError}
            onDismissRetryError={handleDismissRunError}
            performerBulkRunning={performerBulkRunning}
            performerConfig={performerConfig}
            onOpenAiSettings={onOpenAiSettings}
            onClose={() => setRowExpanded(false)}
            onFilterByMeeting={onFilterByMeeting}
            onFilterBySource={onFilterBySource}
            onTitleChange={onTitleChange}
            onNoteChange={onNoteChange}
            onDueDateChange={onDueDateChange}
            onCategoryChange={onCategoryChange}
            categoryOptions={categoryOptions}
            onAccountChange={onAccountChange}
            accountSuggestions={accountSuggestions}
            showAiAssist={showAiAssist}
            updating={updating}
            showSourceMetaRow={false}
            showOriginalSourceSection={false}
            showCreatedFooter={false}
            showFooterActions={false}
            aiIconOnly
            calendarPrefillUrl={calendarPrefillUrl}
            serverCalendarEventUrl={serverCalendarEvent?.event_url || null}
            footerContent={expandedFooterContent}
          />
        </div>
      ) : null}
      {gateModal}
    </div>
  );
};

export default ActionItemRow;
